/**
 * Job Status Badge Component
 *
 * Displays a color-coded badge for job status with animated indicator for active jobs
 */
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { JobStatus } from "@/lib/instantdb";

interface JobStatusBadgeProps {
  status: JobStatus;
  className?: string;
}

const statusConfig: Record<
  JobStatus,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline"; dotColor: string; animated?: boolean }
> = {
  queued: {
    label: "Queued",
    variant: "secondary",
    dotColor: "bg-slate-400",
  },
  uploading: {
    label: "Uploading",
    variant: "outline",
    dotColor: "bg-blue-400",
    animated: true,
  },
  analytics: {
    label: "Analyzing",
    variant: "outline",
    dotColor: "bg-amber-400",
    animated: true,
  },
  analyzing: {
    label: "Analyzing",
    variant: "outline",
    dotColor: "bg-amber-400",
    animated: true,
  },
  rendering: {
    label: "Rendering",
    variant: "outline",
    dotColor: "bg-violet-400",
    animated: true,
  },
  transcoding: {
    label: "Transcoding",
    variant: "outline",
    dotColor: "bg-cyan-400",
    animated: true,
  },
  completed: {
    label: "Completed",
    variant: "default",
    dotColor: "bg-emerald-400",
  },
  failed: {
    label: "Failed",
    variant: "destructive",
    dotColor: "bg-red-400",
  },
};

export function JobStatusBadge({ status, className }: JobStatusBadgeProps) {
  const config = statusConfig[status] ?? statusConfig.queued;

  return (
    <Badge variant={config.variant} className={cn("gap-1.5", className)}>
      <span
        className={cn(
          "h-2 w-2 rounded-full",
          config.dotColor,
          config.animated && "animate-pulse",
        )}
      />
      {config.label}
    </Badge>
  );
}

/**
 * Get a human-readable status message
 */
export function getStatusMessage(status: JobStatus): string {
  switch (status) {
    case "queued":
      return "Waiting in queue...";
    case "uploading":
      return "Uploading videos...";
    case "analytics":
    case "analyzing":
      return "Analyzing content with AI...";
    case "rendering":
      return "Rendering video...";
    case "transcoding":
      return "Transcoding in cloud (webhook pending)...";
    case "completed":
      return "Processing complete";
    case "failed":
      return "Processing failed";
    default:
      return "Unknown status";
  }
}
